import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";

export interface UseCareTeamEnduserIdsResult {
  /** Ids of the patients covered by the selected users, or `undefined` when no care team is selected. */
  enduserIds: string[] | undefined;
  isLoading: boolean;
}

/**
 * Resolves a care-team selection to the ids of the patients those users cover.
 *
 * The API's `_careTeam` pseudo-field does this server-side (`modifyFilterForCareTeam` expands it into
 * an `enduserIds` constraint), but it is only honored on the legacy `filter` param — and
 * `load_threads` accepts `mdbFilter` and `enduserIds` only, never `filter`. So the same expansion is
 * done here and handed to `load_threads` as `enduserIds`, which is exactly what the server would
 * have produced.
 *
 * @param careTeamIds users whose patients should be matched; omit or empty to disable the filter
 */
export const useCareTeamEnduserIds = (careTeamIds: string[] | undefined): UseCareTeamEnduserIdsResult => {
  const isEnabled = !!careTeamIds?.length;

  const { data, isLoading } = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 1000,
      mdbFilter: isEnabled ? { assignedTo: { $in: careTeamIds } } : undefined,
    },
    fetchAllPages: true,
    enabled: isEnabled,
    select: (result) => result.pages.flat().map(({ id }) => id),
  });

  return {
    // A selection that resolves to no patients must still constrain the query, or the filter would
    // silently widen to every thread. An empty list is the correct "matches nothing" signal.
    enduserIds: isEnabled ? data ?? [] : undefined,
    isLoading: isEnabled && isLoading,
  };
};
